import React, { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { Family } from "@/types/models";
import { toast } from "sonner";
import { useDataServices } from "@/hooks/useDataServices";
import { useAuth } from "@/hooks/useAuth";

const familyFormSchema = z.object({
  name: z.string().min(1, "Family name is required"),
});

type FamilyFormValues = z.infer<typeof familyFormSchema>;

interface AddFamilyDialogProps {
  open: boolean;
  onClose: () => void;
  onFamilyAdded: (family: Family, families: Family[]) => void;
}

const AddFamilyDialog: React.FC<AddFamilyDialogProps> = ({
  open,
  onClose,
  onFamilyAdded,
}) => {
  console.log("🔥 [AddFamilyDialog] Component rendering", { open });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { family: familyService } = useDataServices();
  const { organizationId } = useAuth();
  console.log("🔥 [AddFamilyDialog] useAuth called", { organizationId });

  const form = useForm<FamilyFormValues>({
    resolver: zodResolver(familyFormSchema),
    defaultValues: {
      name: "",
    },
  });

  const onSubmit = async (data: FamilyFormValues) => {
    console.log("🔥 [AddFamilyDialog] onSubmit called", { name: data.name, organizationId, isSubmitting });
    if (isSubmitting) return; // Prevent double submit
    setIsSubmitting(true);
    if (!organizationId) {
      toast.error("Organization ID not found");
      setIsSubmitting(false);
      return;
    }

    // Check for duplicate family name before submitting
    console.log("🔥 [AddFamilyDialog] About to call getFamilies...");
    const existingFamilies = await familyService.getFamilies(organizationId);
    console.log("🔥 [AddFamilyDialog] getFamilies completed", existingFamilies.length);
    console.log("[AddFamilyDialog] Existing families:", existingFamilies.map(f => f.name));
    const duplicate = existingFamilies.find(f => f.name.trim().toLowerCase() === data.name.trim().toLowerCase());
    if (duplicate) {
      toast.error("A family with this name already exists.", {
        description: "Please add a distinguishing detail (e.g., first name or initial) to the family name."
      });
      setIsSubmitting(false);
      return;
    }

    console.log("[AddFamilyDialog] Submitting new family:", { name: data.name, organization_id: organizationId });
    try {
      // Using FamilyService.addFamily instead of createFamily
      await familyService.addFamily({
        name: data.name,
        organization_id: organizationId,
      });

      // Force refetch of families after add
      const updatedFamilies = await familyService.getFamilies(organizationId);
      
      // Find the newly created family from the updated list
      const newFamily = updatedFamilies.find(f => f.name === data.name);
      
      if (newFamily) {
        onFamilyAdded(newFamily, updatedFamilies);
        toast.success("Family created successfully");
      } else {
        toast.error("Family was created but could not be found in the updated list");
      }
      
      form.reset();
      onClose();
    } catch (error: any) {
      if (error?.code === '23505') {
        // Retry fetch up to 3 times with delay to handle slow DB propagation
        let exists = false;
        for (let i = 0; i < 3; i++) {
          await new Promise(res => setTimeout(res, 300));
          const updatedFamilies = await familyService.getFamilies(organizationId);
          exists = updatedFamilies.some(f => f.name.trim().toLowerCase() === data.name.trim().toLowerCase());
          if (exists) break;
        }
        if (!exists) {
          toast.error("A family with this name already exists.", {
            description: "Please add a distinguishing detail (e.g., first name or initial) to the family name."
          });
        }
        // If it exists, do nothing (the add succeeded)
      } else {
        console.error("Error creating family:", error);
        toast.error("Failed to create family");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add New Family</DialogTitle>
          <DialogDescription>
            Create a new family in your organization.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Family Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter family name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                variant="outline"
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Create Family
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddFamilyDialog;
